import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { appointmentService } from '../services/appointmentService'
import { Appointment, AppointmentStatus, AppointmentType } from '../types/appointment'
import { AppointmentScheduler } from '../components/AppointmentScheduler'
import { AppointmentCalendar } from '../components/AppointmentCalendar'
import { AppointmentKanban } from '../components/AppointmentKanban'
import { PractitionerAvailability } from '../components/PractitionerAvailability'
import { AppointmentCancelModal } from '../components/AppointmentCancelModal'
import { AppointmentDNAModal } from '../components/AppointmentDNAModal'
import { QuickStartConsultationForm } from '../components/QuickStartConsultationForm'
import { getVisitReasonLabel } from '../constants/visitReasons'
import { StcSigning } from './StcSigning'
import { Modal } from '../components/Modal'
import { patientService } from '../services/patientService'
import { Patient } from '../types/patient'
import { userService } from '../services/userService'
import { practiceLocationService } from '../services/practiceLocationService'
import { useAuth } from '../contexts/AuthContext'
import { mapStcReasonsToSpecialServiceCode } from '../utils/stcReasonToCode'
import { formatPatientName } from '../utils/name'
import './Appointments.css'

type ViewMode = 'calendar' | 'kanban' | 'list' | 'appointments' | 'report'

export function Appointments() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showScheduler, setShowScheduler] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('appointments')
  const [filters, setFilters] = useState<{
    status?: AppointmentStatus[]
    type?: AppointmentType[]
    startDate?: string
    endDate?: string
    staffId?: string
    minDnaCount?: number
  }>({})
  const [cancelModalAppointment, setCancelModalAppointment] = useState<Appointment | null>(null)
  const [dnaModalAppointment, setDnaModalAppointment] = useState<Appointment | null>(null)
  const [consultationModalAppointment, setConsultationModalAppointment] = useState<Appointment | null>(null)
  const [stcModalAppointment, setStcModalAppointment] = useState<Appointment | null>(null)

  const { data: appointments, isLoading, error } = useQuery({
    queryKey: ['appointments', filters],
    queryFn: () => appointmentService.getAll(filters),
    // Only poll automatically when NOT in report view (report should be on-demand)
    refetchInterval: viewMode === 'report' ? false : 5000, // Poll every 5 seconds to get real-time updates from other users
    refetchOnWindowFocus: viewMode !== 'report', // Don't auto-refetch report view on focus
    refetchIntervalInBackground: false, // Only poll when tab is active to save resources
  })

  // Use a dedicated query key so we always load the full list for name lookups.
  // Other pages use ['patients'] with getAll() (no fetchAll), which returns empty from mock;
  // sharing that cache would show "Unknown Patient" here.
  const { data: patientsData } = useQuery({
    queryKey: ['patients', 'all-for-appointments'],
    queryFn: () => patientService.getAllPatients(),
  })
  const patients = patientsData || []

  const { data: clinicalStaff } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
  })

  const { data: practiceLocation } = useQuery({
    queryKey: ['practice-location'],
    queryFn: () => practiceLocationService.getCurrent(),
  })

  const dnaMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.markAsDNA(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'dna' as AppointmentStatus, dnaAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      await queryClient.refetchQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setDnaModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to mark appointment as DNA:', error)
      alert('Failed to mark appointment as DNA. Please try again.')
    },
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.cancel(id, reason),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      // Update all appointment queries (with or without filters)
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      // Get all appointment queries and update them optimistically
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      // Optimistically update all appointment queries
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'cancelled' as AppointmentStatus, cancelledAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      // Refetch all appointment queries to get the latest data from server
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setCancelModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to cancel appointment:', error)
      alert('Failed to cancel appointment. Please try again.')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => appointmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  const handleCancel = (appointment: Appointment) => {
    setCancelModalAppointment(appointment)
  }

  const handleCancelConfirm = async (reason?: string) => {
    if (cancelModalAppointment) {
      await cancelMutation.mutateAsync({ id: cancelModalAppointment.id, reason })
    }
  }

  const handleMarkDNA = (appointment: Appointment) => {
    setDnaModalAppointment(appointment)
  }

  const handleDNAConfirm = async (reason?: string) => {
    if (dnaModalAppointment) {
      await dnaMutation.mutateAsync({ id: dnaModalAppointment.id, reason })
    }
  }

  const arrivedMutation = useMutation({
    mutationFn: (id: string) => appointmentService.markAsArrived(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'arrived' as AppointmentStatus, arrivedAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to mark appointment as arrived:', error)
      alert('Failed to mark appointment as arrived. Please try again.')
    },
  })

  const revertArrivedMutation = useMutation({
    mutationFn: (id: string) => appointmentService.revertArrived(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'scheduled' as AppointmentStatus, arrivedAt: undefined, arrivedBy: undefined }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to revert arrived status:', error)
      alert('Failed to revert arrived status. Please try again.')
    },
  })

  const handleMarkArrived = async (appointment: Appointment) => {
    if (window.confirm(`Mark this appointment as arrived?`)) {
      await arrivedMutation.mutateAsync(appointment.id)
    }
  }

  const handleRevertArrived = async (appointment: Appointment) => {
    if (window.confirm(`Revert this appointment's arrived status?`)) {
      await revertArrivedMutation.mutateAsync(appointment.id)
    }
  }

  const handleDelete = async (appointment: Appointment) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await deleteMutation.mutateAsync(appointment.id)
      } catch (error) {
        alert('Failed to delete appointment')
      }
    }
  }

  const getPatientName = (patientId: string) => {
    const patient = patients.find((p: Patient) => p.id === patientId)
    return formatPatientName(patient ?? null) || 'Unknown Patient'
  }

  const getStaffName = (staffId: string) => {
    const staff = clinicalStaff?.find((s) => s.id === staffId)
    return staff ? `${staff.firstName} ${staff.lastName}` : 'Unknown'
  }

  const formatDateTime = (date: string, time: string) => {
    const dateObj = new Date(date)
    return `${dateObj.toLocaleDateString()} at ${time}`
  }

  const getStatusBadgeClass = (status: AppointmentStatus) => {
    switch (status) {
      case 'scheduled':
        return 'badge-scheduled'
      case 'arrived':
        return 'badge-arrived'
      case 'in-progress':
        return 'badge-in-progress'
      case 'completed':
        return 'badge-completed'
      case 'cancelled':
        return 'badge-cancelled'
      case 'dna':
        return 'badge-dna'
      default:
        return ''
    }
  }

  const handlePrintReport = () => {
    window.print()
  }

  if (isLoading) return <div className="loading">Loading appointments...</div>
  if (error) return <div className="error">Error loading appointments</div>

  return (
    <div className="appointments-page">
      <div className="appointments-header">
        <h1>Appointments</h1>
        <div className="appointments-header-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'appointments' ? 'active' : ''}`}
              onClick={() => setViewMode('appointments')}
              title="Appointments View"
            >
              📆 Appointments
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title="Calendar View"
            >
              📅 Calendar
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
              title="Kanban View"
            >
              📋 Kanban
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              📄 List
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'report' ? 'active' : ''}`}
              onClick={() => setViewMode('report')}
              title="Report View"
            >
              📊 Report
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowScheduler(!showScheduler)}>
            {showScheduler ? 'Cancel' : '+ New Appointment'}
          </button>
        </div>
      </div>

      {showScheduler && (
        <Modal
          isOpen={showScheduler}
          onClose={() => setShowScheduler(false)}
          title="Schedule Appointment"
          size="large"
        >
          <div className="scheduler-container">
            <AppointmentScheduler
              onSuccess={() => {
                setShowScheduler(false)
              }}
              onCancel={() => setShowScheduler(false)}
            />
          </div>
        </Modal>
      )}

      {viewMode === 'report' && (
      <>
      <div className="appointments-report-header">
        <div className="report-title-block">
          <h2>Appointments Report</h2>
          <p className="report-location">
            {practiceLocation
              ? `${practiceLocation.practiceName}${
                  practiceLocation.addresses && practiceLocation.addresses.length > 0
                    ? `, ${practiceLocation.addresses[0].addressLine1}, ${practiceLocation.addresses[0].city}`
                    : ''
                }`
              : 'Clinic Location'}
          </p>
          <p className="report-meta">
            Confidential – Patient Information. Printed by{' '}
            {user ? `${user.firstName} ${user.lastName}` : 'Unknown User'} on{' '}
            {new Date().toLocaleString()}
          </p>
        </div>
        <button className="btn-secondary report-print-button" onClick={handlePrintReport}>
          🖨 Print Report
        </button>
      </div>
      <div className="appointments-filters">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status?.join(',') || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: e.target.value ? (e.target.value.split(',') as AppointmentStatus[]) : undefined,
              })
            }
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="dna">DNA</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Type</label>
          <select
            value={filters.type?.join(',') || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                type: e.target.value ? (e.target.value.split(',') as AppointmentType[]) : undefined,
              })
            }
          >
            <option value="">All Types</option>
            <option value="consultation">Consultation</option>
            <option value="follow-up">Follow-up</option>
            <option value="checkup">Checkup</option>
            <option value="procedure">Procedure</option>
            <option value="emergency">Emergency</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Provider</label>
          <select
            value={filters.staffId || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                staffId: e.target.value || undefined,
              })
            }
          >
            <option value="">All Providers</option>
            {clinicalStaff
              ?.filter((s) => s.roleCategory === 'Clinical')
              .map((staff) => (
                <option key={staff.id} value={staff.id}>
                  {staff.firstName} {staff.lastName} ({staff.role})
                </option>
              ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value || undefined })}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value || undefined })}
          />
        </div>
        {filters.status?.includes('dna') && (
          <div className="filter-group">
            <label>Min DNA Count</label>
            <input
              type="number"
              min="0"
              value={filters.minDnaCount || ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  minDnaCount: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              placeholder="0"
            />
            <small>Show patients with this many or more DNA'd appointments</small>
          </div>
        )}
        <div className="filter-actions">
          <button className="btn-secondary" onClick={() => setFilters({})}>
            Clear Filters
          </button>
        </div>
      </div>
      </>
      )}

      {!appointments || appointments.length === 0 ? (
        <div className="empty-state">
          <p>No appointments found.</p>
          <button className="btn-primary" onClick={() => setShowScheduler(true)}>
            Schedule First Appointment
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'calendar' && (
            <AppointmentCalendar
              appointments={appointments}
              patients={patients || []}
              staff={clinicalStaff || []}
              onDateClick={(_date) => {
                setShowScheduler(true)
                // Could pre-fill the date in the scheduler
              }}
              onEventClick={(appointment) => {
                navigate(`/patients/${appointment.patientId}`)
              }}
            />
          )}
          {viewMode === 'appointments' && (
            <PractitionerAvailability
              appointments={appointments}
              patients={patients || []}
              staff={clinicalStaff || []}
              onAppointmentClick={(appointment) => {
                navigate(`/patients/${appointment.patientId}`)
              }}
            />
          )}
          {viewMode === 'kanban' && (
            <AppointmentKanban
              appointments={appointments}
              patients={patients || []}
              staff={clinicalStaff || []}
              onAppointmentClick={(appointment) => {
                navigate(`/patients/${appointment.patientId}`)
              }}
            />
          )}
          {viewMode === 'list' && (
            <div className="appointments-list">
              {appointments.map((appointment) => (
                <div key={appointment.id} className="appointment-card">
                  <div className="appointment-header">
                    <div>
                      <h3>
                        {getPatientName(appointment.patientId)} with {getStaffName(appointment.staffId)}
                      </h3>
                      <p className="appointment-datetime">
                        {formatDateTime(appointment.appointmentDate, appointment.startTime)} -{' '}
                        {appointment.endTime}
                      </p>
                    </div>
                    <span className={`badge ${getStatusBadgeClass(appointment.status)}`}>
                      {appointment.status}
                    </span>
                    {appointment.status === 'completed' && appointment.stcSuggested && (
                      appointment.stcRaised ? (
                        <span
                          className="badge stc-indicator-badge stc-indicator-badge-disabled"
                          title={appointment.stcRaisedAt ? `STC signed ${new Date(appointment.stcRaisedAt).toLocaleString()}` : 'STC signed'}
                          aria-label="STC signed"
                        >
                          STC
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="badge stc-indicator-badge stc-indicator-badge-button"
                          title={appointment.stcSuggestedReasons?.length ? `STC signature needed: ${appointment.stcSuggestedReasons.join(', ')}` : 'STC signature needed'}
                          aria-label="STC signature required – open form"
                          onClick={() => setStcModalAppointment(appointment)}
                        >
                          STC
                        </button>
                      )
                    )}
                  </div>
                  <div className="appointment-details">
                    <div className="detail-item">
                      <label>Type</label>
                      <p>{appointment.type}</p>
                    </div>
                    <div className="detail-item">
                      <label>Duration</label>
                      <p>{appointment.duration} minutes</p>
                    </div>
                    {(appointment.reason || appointment.reasonCode) && (
                      <div className="detail-item full-width">
                        <label>Reason</label>
                        <p>{appointment.reason || getVisitReasonLabel(appointment.reasonCode)}</p>
                      </div>
                    )}
                    {appointment.notes && (
                      <div className="detail-item full-width">
                        <label>Notes</label>
                        <p>{appointment.notes}</p>
                      </div>
                    )}
                    {(() => {
                      const patient = patients.find((p: Patient) => p.id === appointment.patientId)
                      if (patient && patient.dnaCount && patient.dnaCount > 0 && appointment.status !== 'dna') {
                        const lastDna = patient.dnaHistory?.[0]
                        return (
                          <div className="detail-item full-width">
                            <div className="dna-warning-alert">
                              <strong>⚠️ DNA History:</strong> {patient.dnaCount === 1 
                                ? 'DNA\'d last appointment' 
                                : `DNA'd last ${patient.dnaCount} appointment${patient.dnaCount > 1 ? 's' : ''}`}
                              {lastDna && !lastDna.dnaReason && (
                                <span className="dna-no-reason"> (last without reason)</span>
                              )}
                            </div>
                          </div>
                        )
                      }
                      return null
                    })()}
                  </div>
                  <div className="appointment-actions">
                    <button
                      className="btn-link"
                      onClick={() => navigate(`/patients/${appointment.patientId}`)}
                    >
                      View Patient
                    </button>
                    {appointment.status !== 'cancelled' && appointment.status !== 'completed' && appointment.status !== 'dna' && appointment.status !== 'in-progress' && (
                      <>
                        {appointment.status === 'arrived' ? (
                          <>
                            <button
                              className="btn-link btn-primary"
                              onClick={() => setConsultationModalAppointment(appointment)}
                            >
                              Start Consultation
                            </button>
                            <button
                              className="btn-link"
                              onClick={() => handleRevertArrived(appointment)}
                              disabled={revertArrivedMutation.isPending}
                            >
                              Revert Arrived
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-link btn-success"
                              onClick={() => handleMarkArrived(appointment)}
                              disabled={arrivedMutation.isPending}
                            >
                              Mark as Arrived
                            </button>
                            <button
                              className="btn-link btn-warning"
                              onClick={() => handleMarkDNA(appointment)}
                              disabled={dnaMutation.isPending}
                            >
                              Mark as DNA
                            </button>
                            <button
                              className="btn-link btn-warning"
                              onClick={() => handleCancel(appointment)}
                              disabled={cancelMutation.isPending}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        <button
                          className="btn-link btn-danger"
                          onClick={() => handleDelete(appointment)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {appointment.status === 'cancelled' && appointment.cancellationReason && (
                      <div className="cancellation-reason">
                        <strong>Cancellation reason:</strong> {appointment.cancellationReason}
                      </div>
                    )}
                    {appointment.status === 'dna' && (
                      <div className="dna-info">
                        <strong>DNA:</strong> {appointment.dnaReason || 'No reason provided'}
                        {appointment.dnaAt && (
                          <span className="dna-date">
                            {' '}({new Date(appointment.dnaAt).toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {viewMode === 'report' && (
            <>
            <div className="appointments-report">
              <table className="appointments-report-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Patient</th>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {[...appointments]
                    .slice()
                    .sort((a, b) => {
                      const aDate = new Date(`${a.appointmentDate}T${a.startTime}`)
                      const bDate = new Date(`${b.appointmentDate}T${b.startTime}`)
                      return aDate.getTime() - bDate.getTime()
                    })
                    .map((appointment) => (
                      <tr key={appointment.id}>
                        <td>{new Date(appointment.appointmentDate).toLocaleDateString('en-GB')}</td>
                        <td className="report-time-cell">
                          {appointment.startTime}-{appointment.endTime}
                        </td>
                        <td>{getPatientName(appointment.patientId)}</td>
                        <td>{getStaffName(appointment.staffId)}</td>
                        <td>{appointment.type}</td>
                        <td>{appointment.status}</td>
                        <td>{appointment.duration}min</td>
                        <td>{appointment.reason || getVisitReasonLabel(appointment.reasonCode) || ''}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="appointments-report-footer">
              <p className="report-footer-text">
                Confidential – Patient Information. Printed by{' '}
                {user ? `${user.firstName} ${user.lastName}` : 'Unknown User'} on{' '}
                {new Date().toLocaleString()}
              </p>
            </div>
            </>
          )}
        </>
      )}

      {cancelModalAppointment && (
        <AppointmentCancelModal
          appointment={{
            id: cancelModalAppointment.id,
            patientName: getPatientName(cancelModalAppointment.patientId),
            staffName: getStaffName(cancelModalAppointment.staffId),
            appointmentDate: cancelModalAppointment.appointmentDate,
            startTime: cancelModalAppointment.startTime,
            type: cancelModalAppointment.type,
          }}
          isOpen={!!cancelModalAppointment}
          onClose={() => setCancelModalAppointment(null)}
          onConfirm={handleCancelConfirm}
          isLoading={cancelMutation.isPending}
        />
      )}

      {dnaModalAppointment && (
        <AppointmentDNAModal
          appointment={{
            id: dnaModalAppointment.id,
            patientName: getPatientName(dnaModalAppointment.patientId),
            staffName: getStaffName(dnaModalAppointment.staffId),
            appointmentDate: dnaModalAppointment.appointmentDate,
            startTime: dnaModalAppointment.startTime,
            type: dnaModalAppointment.type,
          }}
          isOpen={!!dnaModalAppointment}
          onClose={() => setDnaModalAppointment(null)}
          onConfirm={handleDNAConfirm}
          isLoading={dnaMutation.isPending}
        />
      )}

      {consultationModalAppointment && (
        <Modal
          isOpen={!!consultationModalAppointment}
          onClose={() => setConsultationModalAppointment(null)}
          title="Start Consultation"
          size="medium"
        >
          <QuickStartConsultationForm
            patientId={consultationModalAppointment.patientId}
            appointmentId={consultationModalAppointment.id}
            initialStaffId={consultationModalAppointment.staffId}
            initialReason={consultationModalAppointment.reason}
            initialReasonCode={consultationModalAppointment.reasonCode}
            initialPregnancyId={consultationModalAppointment.pregnancyId}
            onCancel={() => setConsultationModalAppointment(null)}
          />
        </Modal>
      )}

      {stcModalAppointment && stcModalAppointment.stcSuggestedConsultationId && (
        <Modal
          isOpen={!!stcModalAppointment}
          onClose={() => setStcModalAppointment(null)}
          title={`STC – ${getPatientName(stcModalAppointment.patientId)}`}
          size="large"
        >
          <div className="stc-modal-body">
            <StcSigning
              embedded
              consultationIdProp={stcModalAppointment.stcSuggestedConsultationId}
              patientIdProp={stcModalAppointment.patientId}
              patientNameProp={getPatientName(stcModalAppointment.patientId)}
              initialLocationProp="S"
              initialSpecialServiceProp={mapStcReasonsToSpecialServiceCode(stcModalAppointment.stcSuggestedReasons)}
              appointmentIdProp={stcModalAppointment.id}
              onClose={() => setStcModalAppointment(null)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['stc-records'] })
                queryClient.invalidateQueries({ queryKey: ['appointments'] })
                setStcModalAppointment(null)
              }}
            />
            <p className="stc-modal-link">
              <Link to={`/patients/${stcModalAppointment.patientId}`} onClick={() => setStcModalAppointment(null)}>
                View patient record
              </Link>
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}
